import { useState } from 'react';
import { TshirtSelector } from './TshirtSelector';
import { CopyButton } from './CopyButton';
import { createChiffrage } from '../services/api';
import { UNIVERS, ROLES, calculateJours, type Univers, type TailleShirt, type CreateChiffrageInput } from '../types';
import './ChiffrageForm.css';

interface ChiffrageFormProps {
    onCreated: () => void;
}

export function ChiffrageForm({ onCreated }: ChiffrageFormProps) {
    const [univers, setUnivers] = useState<Univers>('Vente');
    const [sujet, setSujet] = useState('');
    const [tailles, setTailles] = useState<Record<string, TailleShirt>>({
        cx: '—',
        lead_ux: '—',
        lead_ui: '—',
        product: '—',
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Calcul des jours
    const joursParRole = {
        cx: calculateJours(tailles.cx, 'cx'),
        lead_ux: calculateJours(tailles.lead_ux, 'lead_ux'),
        lead_ui: calculateJours(tailles.lead_ui, 'lead_ui'),
        product: calculateJours(tailles.product, 'product'),
    };
    const totalJours = Object.values(joursParRole).reduce((a, b) => a + b, 0);

    // Texte à copier
    const texteToCopy = `Bonjour,

Concernant le sujet "${sujet || '[À compléter]'}", le budget Design requis est de ${totalJours} jour${totalJours !== 1 ? 's' : ''}.

Merci de nous communiquer la ligne Orchestra une fois ouverte pour que nous puissions procéder à l'attribution des jours.

Bien à vous,`;

    const handleTailleChange = (role: string, value: TailleShirt) => {
        setTailles(prev => ({ ...prev, [role]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!sujet.trim()) {
            alert('Veuillez renseigner un sujet');
            return;
        }

        setLoading(true);
        try {
            const input: CreateChiffrageInput = {
                univers,
                sujet: sujet.trim(),
                taille_cx: tailles.cx,
                taille_lead_ux: tailles.lead_ux,
                taille_lead_ui: tailles.lead_ui,
                taille_product: tailles.product,
            };

            await createChiffrage(input);

            // Reset form
            setSujet('');
            setTailles({ cx: '—', lead_ux: '—', lead_ui: '—', product: '—' });
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);

            onCreated();
        } catch (err) {
            console.error('Erreur création:', err);
            alert('Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="chiffrage-form" onSubmit={handleSubmit}>
            <div className="form-header">
                <h2>Nouveau chiffrage</h2>
                <p>Renseignez les informations pour calculer le budget Design</p>
            </div>

            <div className="form-grid">
                <div className="form-group">
                    <label htmlFor="univers">Univers</label>
                    <select
                        id="univers"
                        value={univers}
                        onChange={(e) => setUnivers(e.target.value as Univers)}
                    >
                        {UNIVERS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group form-group-wide">
                    <label htmlFor="sujet">Sujet</label>
                    <input
                        id="sujet"
                        type="text"
                        value={sujet}
                        onChange={(e) => setSujet(e.target.value)}
                        placeholder="Décrivez le sujet du chiffrage..."
                        required
                    />
                </div>
            </div>

            <div className="form-section">
                <h3>Force de cadrage par rôle</h3>
                <div className="tshirt-grid">
                    {ROLES.map(({ key, label }) => (
                        <TshirtSelector
                            key={key}
                            role={key}
                            roleLabel={label}
                            value={tailles[key]}
                            onChange={(value) => handleTailleChange(key, value)}
                        />
                    ))}
                </div>
            </div>

            <div className="form-total">
                <div className="total-breakdown">
                    <span className="total-label">Total</span>
                    <div className="total-details">
                        {ROLES.map(({ key, label }) => (
                            <span key={key} className="detail-item">
                                {label}: <strong>{joursParRole[key as keyof typeof joursParRole]}</strong>
                            </span>
                        ))}
                    </div>
                </div>
                <div className="total-value">
                    <span className="total-number">{totalJours}</span>
                    <span className="total-unit">jour{totalJours !== 1 ? 's' : ''}</span>
                </div>
            </div>

            <div className="form-preview">
                <h4>Texte généré</h4>
                <pre className="preview-text">{texteToCopy}</pre>
                <CopyButton text={texteToCopy} />
            </div>

            <div className="form-actions">
                <button
                    type="submit"
                    className={`submit-button ${loading ? 'loading' : ''} ${success ? 'success' : ''}`}
                    disabled={loading}
                >
                    {loading ? 'Création...' : success ? '✓ Créé !' : 'Créer la ligne'}
                </button>
            </div>
        </form>
    );
}
